import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';

import { ISOCurrencyCode, ObjectivesCurrenciesConfig } from '../../../types';

interface Props {
  value?: ISOCurrencyCode | null;
  onChange: (code: ISOCurrencyCode) => void;
  disabled?: boolean;
  currenciesConfig: ObjectivesCurrenciesConfig;
}

const getCurrencyName = (code: string, locale: string): string => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: 'currency' }).of(code) ?? code
    );
  } catch {
    return code;
  }
};

const getCurrencySymbol = (code: string, locale: string): string => {
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency: code })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? code
    );
  } catch {
    return code;
  }
};

const formatCurrencyLabel = (code: string, locale: string): string =>
  `${getCurrencyName(code, locale)} - ${getCurrencySymbol(code, locale)}`;

// NOTE: Reactstrap Dropdown was considered but has rendering issues in this
// context. A plain Input type="select" is simpler and works well here.
const CurrencySelector: FC<Props> = ({
  value,
  onChange,
  disabled,
  currenciesConfig,
}) => {
  const { locale, formatMessage } = useIntl();

  const currentValue = value || currenciesConfig.default;

  // Build the list of currency codes to display, sorted by localized name.
  // Preserves a legacy code already stored on an objective even if removed
  // from the allowed list.
  const displayCodes = useMemo(() => {
    const codes = [...currenciesConfig.allowed];
    if (currentValue && !codes.includes(currentValue)) {
      codes.push(currentValue);
    }
    return codes.sort((a, b) =>
      getCurrencyName(a, locale).localeCompare(getCurrencyName(b, locale))
    );
  }, [currenciesConfig.allowed, currentValue, locale]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <Input
      type="select"
      bsSize="sm"
      className="form-control-sm"
      value={currentValue}
      onChange={handleChange}
      disabled={disabled}
      aria-label={formatMessage({
        id: 'app.views.objectives.currency_selector.aria_label',
        defaultMessage: 'Select currency',
      })}
    >
      {displayCodes.map((code) => (
        <option key={code} value={code}>
          {formatCurrencyLabel(code, locale)}
        </option>
      ))}
    </Input>
  );
};

export { getCurrencySymbol };
export default CurrencySelector;
